// Quote Builder — right-column "Add item" fuzzy search.
//
// Typing a partial description finds a product without going through the
// left-hand pickers, for quicker quoting (Steve, 2026-08-19). RIGHT COLUMN
// ONLY: the left column's purpose is still undecided, so nothing here touches
// it, and the auto-add-matching-door-on-cabinet-add idea is parked separately
// — it needs a retailer-level sourcing-model setting first.
//
// Matches on product name, code, door range, colour, category and supplier. A
// size typed into the query ("cartmel 715x596") is parsed out and used to
// narrow the results and set the line's dimensions, rather than being matched
// as text, which would otherwise wreck the score.
//
// PRICED (2026-08-19, second pass). Results come from /api/catalogue/search,
// which already searches catalogue_products across every supplier the retailer
// has been granted and returns list price, dimensions, range, colour, category
// and supplier. The added line therefore carries a real price, the retailer's
// per-supplier discount (resolveDiscPct, the same resolver the pickers use) and
// the quote's default markup — identical to adding it from a picker.
//
// The server matches by AND-ed word tokens; the fuzzy pass here RE-RANKS that
// candidate set so a near-miss still surfaces sensibly. Both are needed: the
// server orders by product code, fuzzy alone has nothing to rank without the
// fetch.
//
// TRUNCATION FIX (2026-08-26). The re-rank can only reorder what the fetch
// returned, and the fetch was returning the wrong 60 rows: ordered by product
// code, a range's colours sit contiguously, so the window closed inside the
// first one. "cartmel" matched 1,122 rows and came back 60× Cartmel Light Grey;
// "vivo" matched 1,627 across 15 range+colour groups and came back 60× Vivo
// Gloss White, containing not one of the 15 doors that actually match
// "vivo 715x596". Two server-side changes fix it, both requested here:
// `groupBy=range_colour` spreads the row budget across groups round-robin, and
// `w`/`h` filter the size in SQL.
//
// MULTI-WORD FIX (2026-08-27). The truncation fix assumed multi-word queries
// would then fall out of the fuzzy pass. They did not: the server was still
// matching `q` as one contiguous substring and did not look at colour at all,
// so "vivo light grey" matched zero rows, fell through to the longest-word
// retry, and returned 2 lighting pelmets. Adding words made results FEWER, not
// more targeted, because the candidate set was picked by one arbitrary word and
// the fuzzy AND then discarded nearly all of it.
//
// The server now tokenises `q` across code/name/doorRange/colour, and drops the
// round-robin once the query resolves to a single range+colour — so naming a
// colour spends the whole row budget on that door's SIZES, which is the actual
// retailer workflow (search range + finish, then narrow by size).

import { api } from "@tkos/api-client";
import type { QuoteStore } from "./state";
import type { QuoteItem } from "./types";
import { escapeHtml, newId } from "./formatters";
import {
  classifyCatalogueHit,
  composeCatalogueDesc,
  type CatalogueRowFacts,
} from "./catalogue-classify";
import { fuzzyFilter, bumpFav } from "./fuzzy";
import { supplierLabelForSlug } from "./supplier-label";
import { recalcItem } from "./pricing";
import { loadDiscountsFor, resolveDiscPct } from "./discounts";

/** One row of /api/catalogue/search. */
export interface CatalogueHit {
  id: string;
  sku: string;
  desc: string;
  price: number;
  h: number;
  w: number;
  d: number;
  range: string;
  colour: string;
  category: string;
  supplierSlug: string;
  supplierName: string;
  /** The supplier's discount code (PWS "BMV"); "" when the catalogue has none. */
  discountCode?: string;
}

/** Max results shown. Deliberately short — this is a "find the thing I mean"
 *  box, not a catalogue browser; that is what the left-hand pickers are for. */
const RESULT_LIMIT = 8;

/** Dimensions parsed out of a free-text query. */
interface SizeHint { width: number; height: number; rest: string }

/** Pull a size out of the query and return it separately from the remaining
 *  words. Understands "715x596", "715 x 596", "715×596" and a bare "715".
 *
 *  The two numbers are NOT assigned an axis here, and the field names below are
 *  just "first" and "second" by another name. The catalogue names a door
 *  height-first ("715 X 596" is stored h=715, w=596) while retailers type and
 *  think width-first, so both the server filter and the fallback below match
 *  each number against EITHER axis rather than guessing which order was meant.
 *  A single number is treated the same way. */
export function parseSizeHint(query: string): SizeHint {
  const pair = query.match(/(\d{2,4})\s*[x×*]\s*(\d{2,4})/i);
  if (pair?.[1] && pair[2]) {
    return {
      width: Number(pair[1]),
      height: Number(pair[2]),
      rest: query.replace(pair[0], " ").replace(/\s+/g, " ").trim(),
    };
  }
  const single = query.match(/(?:^|\s)(\d{3,4})(?:\s|$)/);
  if (single?.[1]) {
    return {
      width: Number(single[1]),
      height: 0,
      rest: query.replace(single[0], " ").replace(/\s+/g, " ").trim(),
    };
  }
  return { width: 0, height: 0, rest: query.trim() };
}

/** Everything about a catalogue row worth matching against, as one string. */
function searchText(h: CatalogueHit): string {
  return [h.desc, h.sku, h.range, h.colour, h.category, h.supplierName]
    .filter(Boolean).join(" ");
}

/** Classifier + description inputs for one row of /api/catalogue/search.
 *
 *  MOVED TO THE SHARED MODULE (2026-08-28). Both were local to this file and
 *  both were wrong in ways the left panel was wrong differently:
 *
 *  - The old category map tested `raw.includes("fascia")` FIRST, so PWS's
 *    "Fascia Accessories" — 3,440 plinths, cornices, corner posts and end
 *    panels — were all filed as `doors`.
 *  - The old lineDesc prefixed `range` only, so a TKC door came out as
 *    "Vivo Gloss 110 X 596": style but no finish, because TKC splits the two
 *    and PWS does not.
 *
 *  catalogue-classify.ts now owns both decisions for this box AND the left
 *  panel, so the two can no longer drift apart. */
function factsFor(h: CatalogueHit): CatalogueRowFacts {
  return {
    name: h.desc || h.sku || "Item",
    category: h.category || "",
    doorRange: h.range || "",
    colour: h.colour || "",
  };
}

function fmtPrice(n: number): string {
  return `£${(Number(n) || 0).toFixed(2)}`;
}

function resultRowHtml(h: CatalogueHit, idx: number, active: boolean): string {
  const size = h.w && h.h ? `${h.w}×${h.h}mm` : h.w ? `${h.w}mm` : h.h ? `${h.h}mm` : "";
  // The SAME label the line will carry, not the catalogue's raw name — a row
  // that reads "TKC Components" and then lands on the quote as "TKC" is two
  // answers to one question.
  const meta = [supplierLabelForSlug(h.supplierSlug, h.supplierName), h.range, h.category, size]
    .filter(Boolean).join(" · ");
  return `
    <li class="qb-adds__row${active ? " is-active" : ""}" role="option"
        aria-selected="${active ? "true" : "false"}" data-idx="${idx}" id="qb-adds-opt-${idx}">
      <span class="qb-adds__line">
        <span class="qb-adds__name">${escapeHtml(composeCatalogueDesc(factsFor(h)))}</span>
        <span class="qb-adds__price">${fmtPrice(h.price)}</span>
      </span>
      ${meta ? `<span class="qb-adds__meta">${escapeHtml(meta)}</span>` : ""}
    </li>
  `;
}

export interface AddItemSearchHandle { destroy: () => void }

/** Candidates fetched per keystroke before the fuzzy re-rank. Wider than
 *  RESULT_LIMIT so the re-rank has something to actually reorder. */
const FETCH_LIMIT = 60;
/** Typeahead debounce. The endpoint answers in ~50ms over ~40k rows, so this
 *  is about not firing a request per keystroke, not about latency. */
const DEBOUNCE_MS = 180;

/**
 * Mount the search box into a zone's footer.
 *
 * @param host    element to render into
 * @param store   quote store
 * @param zoneId  zone the chosen line is appended to
 * @param onAdded called after a line is added so the caller can re-render
 */
export function mountAddItemSearch(
  host: HTMLElement,
  store: QuoteStore,
  zoneId: string,
  onAdded: () => void,
): AddItemSearchHandle {
  let results: CatalogueHit[] = [];
  let active = 0;
  let open = false;
  let timer: number | null = null;
  // Monotonic request id: a slow response for an earlier query must never
  // overwrite the results of a later one.
  let seq = 0;
  let destroyed = false;

  host.innerHTML = `
    <div class="qb-adds">
      <input type="text" class="qb-adds__input" role="combobox" aria-expanded="false"
             aria-controls="qb-adds-list" aria-autocomplete="list"
             placeholder="Search products — name, code, range, or size…" autocomplete="off" />
      <ul class="qb-adds__list" id="qb-adds-list" role="listbox" hidden></ul>
    </div>
  `;
  const input = host.querySelector<HTMLInputElement>(".qb-adds__input")!;
  const list = host.querySelector<HTMLUListElement>(".qb-adds__list")!;

  function close(): void {
    open = false;
    list.hidden = true;
    list.innerHTML = "";
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  function render(): void {
    if (!results.length) { close(); return; }
    open = true;
    list.hidden = false;
    input.setAttribute("aria-expanded", "true");
    input.setAttribute("aria-activedescendant", `qb-adds-opt-${active}`);
    list.innerHTML = results.map((h, i) => resultRowHtml(h, i, i === active)).join("");
  }

  /** One trip to the catalogue.
   *
   *  `groupBy=range_colour` is what makes a range's other colours reachable at
   *  all: without it the server orders by product code and the 60-row window
   *  lands entirely inside the first colour, so "cartmel" could only ever
   *  return Cartmel Light Grey (2026-08-26). The size, when one was typed, is
   *  filtered in SQL for the same reason — narrowing here cannot recover a row
   *  the LIMIT already threw away. */
  async function fetchHits(term: string, size?: SizeHint): Promise<CatalogueHit[]> {
    const qs = new URLSearchParams({
      q: term,
      limit: String(FETCH_LIMIT),
      groupBy: "range_colour",
    });
    if (size?.width) qs.set("w", String(size.width));
    if (size?.height) qs.set("h", String(size.height));
    try {
      const data = await api<{ results?: CatalogueHit[] }>(`/catalogue/search?${qs.toString()}`);
      return Array.isArray(data.results) ? data.results : [];
    } catch {
      return [];
    }
  }

  async function runSearch(): Promise<void> {
    const raw = input.value.trim();
    if (raw.length < 2) { results = []; close(); return; }
    const size = parseSizeHint(raw);
    const { rest, width } = size;
    const mine = ++seq;

    // LONGEST-WORD RETRY REMOVED (2026-08-27). The server now tokenises `q` and
    // ANDs the words across code/name/doorRange/colour, so a multi-word query
    // matches directly and there is nothing left to retry for.
    //
    // It also had to go on its own merits: it picked the retry word by LENGTH,
    // which correlates with nothing. "vivo light grey" retried on "light" — the
    // longest word at 5 characters — and returned lighting pelmets, discarding
    // the one word ("vivo", 4) that named the range. It only ever looked right
    // because "cartmel indigo", the example it was written against, happens to
    // have the range name as its longest word.
    let hits = await fetchHits(rest || raw, size);
    // Only now drop the size. Handles, sinks, hinges and most non-door rows
    // carry no dimensions at all, so "handle 715" filtered on size matches
    // nothing — falling back to the text-only search keeps those searchable.
    // Deliberately last: a size that DOES exist is never quietly ignored, which
    // is what the old client-side "if nothing matched, keep everything" filter
    // did (it is gone — the server owns this now).
    if (!hits.length && (size.width || size.height)) hits = await fetchHits(rest || raw);
    // A size on its own is a legitimate search ("715") — fall back to matching
    // it as text so the size-only case still returns the right fronts.
    if (!hits.length && !rest && width) hits = await fetchHits(String(width));
    if (destroyed || mine !== seq) return; // superseded by a later keystroke

    results = rest
      ? fuzzyFilter(rest, hits, searchText, {
          limit: RESULT_LIMIT,
          favKey: (h) => h.id || h.sku,
        }).map((f) => f.item)
      : hits.slice(0, RESULT_LIMIT);
    active = 0;
    render();
    // Warm the discount cache for whatever suppliers came back, so the price
    // is already right by the time one is picked.
    for (const slug of new Set(results.map((h) => h.supplierSlug).filter(Boolean))) {
      void loadDiscountsFor(slug);
    }
  }

  function choose(idx: number): void {
    const h = results[idx];
    if (!h) return;
    const { width, height } = parseSizeHint(input.value);
    // Same resolver, same precedence, same "no explicit discount → 0%" rule the
    // pickers use. Resolved from the cache warmed during the search; a miss
    // simply means no configured discount, i.e. trade = list.
    const discPct = resolveDiscPct(h.supplierSlug, h.category || null, h.range || null, {
      discountCode: h.discountCode || null, doorColour: h.colour || null, code: h.sku || null,
    }) ?? 0;
    let item: QuoteItem = {
      id: newId("i"),
      category: classifyCatalogueHit(factsFor(h)),
      // NOT h.supplierName. That is the catalogue's own spelling
      // ("TKC Components") where every tab and every other quote line says
      // "TKC", and a purchase order matches suppliers on the string — so the
      // raw column split one supplier into two. One live line was written that
      // way before this was caught. See supplier-label.ts.
      supplier: supplierLabelForSlug(h.supplierSlug, h.supplierName) || "Manual",
      code: h.sku || "",
      desc: composeCatalogueDesc(factsFor(h)),
      qty: 1,
      // Prefer the catalogue's own dimensions; fall back to what was typed.
      width: h.w || width || 0,
      height: h.h || height || 0,
      depth: h.d || 0,
      notes: "",
      listPrice: Number(h.price) || 0,
      discPct,
      tradePrice: 0,
      markupMode: "pct",
      markupPct: store.get().defaults.markupPct ?? 0,
      markupGbp: 0,
      retailPrice: 0,
      ...(h.range ? { doorRange: h.range } : {}),
      ...(h.colour ? { doorColour: h.colour } : {}),
    };
    // Derive trade + retail from list/discount/markup exactly as a line edit
    // would, rather than leaving them at 0 for the totals to misread.
    item = recalcItem(item, "listPrice");
    store.set((d) => {
      const z = d.zones.find((zo) => zo.id === zoneId);
      if (!z) return;
      z.items.push(item);
    });
    // Teach the shared favourites ranking, same as the pickers do.
    bumpFav(h.id || h.sku);
    input.value = "";
    results = [];
    close();
    onAdded();
  }

  const onInput = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = window.setTimeout(() => { timer = null; void runSearch(); }, DEBOUNCE_MS);
  };
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") { close(); input.blur(); return; }
    if (!open || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      active = (active + 1) % results.length;
      render();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      active = (active - 1 + results.length) % results.length;
      render();
    } else if (e.key === "Enter") {
      e.preventDefault();
      choose(active);
    }
  };
  // `mousedown`, not `click`: the input's blur would close the list first and
  // the click would land on nothing.
  const onListMouseDown = (e: MouseEvent): void => {
    const row = (e.target as HTMLElement).closest<HTMLElement>("[data-idx]");
    if (!row) return;
    e.preventDefault();
    choose(Number(row.dataset["idx"]));
  };
  const onBlur = (): void => { window.setTimeout(close, 0); };

  input.addEventListener("input", onInput);
  input.addEventListener("keydown", onKeyDown);
  input.addEventListener("blur", onBlur);
  list.addEventListener("mousedown", onListMouseDown);

  return {
    destroy(): void {
      destroyed = true;
      if (timer !== null) window.clearTimeout(timer);
      input.removeEventListener("input", onInput);
      input.removeEventListener("keydown", onKeyDown);
      input.removeEventListener("blur", onBlur);
      list.removeEventListener("mousedown", onListMouseDown);
    },
  };
}
